import type { UserGetSubtreeResponse } from 'api/user/User.schemas.ts';
import debounce from 'awesome-debounce-promise';
import { emptyToUndefined, GET, isValidEmail, MPOST, MPUT, POST } from 'common/helpers.ts';
import React from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation';
import type { ROLE, Role } from 'types/Role.ts';
import type { User } from 'types/User.ts';
import { Button } from 'ui/component/Button.tsx';
import { ICheck } from 'ui/component/Icons.tsx';
import { Modal } from 'ui/component/Modal.tsx';
import { Spinner } from 'ui/component/Spinner.tsx';
import { Checkbox } from 'ui/control/Checkbox.tsx';
import { Input } from 'ui/control/Input.tsx';
import { SelectMulti } from 'ui/control/SelectMulti.tsx';
import { getTranslatedError } from '#admin/helpers.ts';
import { useMe } from '#admin/hook/useMe.tsx';

type FormValues = {
  id?: string;
  logistics_ex_id: string;
  name: string;
  email: string;
  phone?: string;
  vat_number: string;
  address: string;
  role: ROLE | '';
  owner_id: string;
  send_credentials: boolean;
};

export type GroupFormProps = {
  userId?: string;
  onSave?: (group: User) => void;
  onClose?: () => void;
  isOpen: boolean;
};

export const UserForm = ({ userId, onSave, onClose, isOpen }: GroupFormProps) => {
  const { t } = useTranslation();
  const { me } = useMe();
  const isEdit = !!userId;
  const thisUserSWR = useSWR<User>(isEdit ? [`/user/${userId}`] : null, GET);
  const usersSWR = useSWR<UserGetSubtreeResponse>(me ? [`/user/${me.owner_id || me.id}/subtree`] : null, GET);
  const rolesSWR = useSWR<Role[]>(['/role/list', { sort: [['name', 'asc']] }], POST);
  const usersSubtree = usersSWR.data;
  const thisUser = thisUserSWR.data;
  const allRoles = rolesSWR.data;

  const createMutation = useSWRMutation('/user', MPOST);
  const updateMutation = useSWRMutation(`/user/${userId}`, MPUT);
  const existsMutation = useSWRMutation('/user/exists', MPOST);

  const isLoading = !!(createMutation.isMutating || updateMutation.isMutating || usersSWR.isValidating || (userId && thisUserSWR.isValidating) || rolesSWR.isValidating);
  const isError = createMutation.error || updateMutation.error || usersSWR.error || (userId && thisUserSWR.error);

  const availableParents = React.useMemo(() => {
    if (!usersSubtree) return {};
    const options: Record<string, string> = {};
    for (const group of usersSubtree.filter((user) => user.is_group).sort((a, b) => a.name.localeCompare(b.name))) {
      options[group.id] = `${group.name} (role=${group.role})`;
    }
    return options;
  }, [usersSubtree]);

  const availableRoles = React.useMemo(() => {
    if (!allRoles) return [];
    const roles = allRoles.map((role) => role.name);
    return roles;
  }, [allRoles]);

  const defaultValues = React.useMemo(
    () => ({
      id: thisUser?.id,
      name: thisUser?.name || '',
      email: thisUser?.email || '',
      role: thisUser?.role || 'MEMBER',
      logistics_ex_id: thisUser?.logistics_ex_id || '',
      phone: thisUser?.phone || '',
      vat_number: thisUser?.vat_number || '',
      address: thisUser?.address || '',
      owner_id: thisUser?.owner_id || '',
      send_credentials: false,
    }),
    [thisUser],
  );

  const formMethods = useForm<FormValues>({
    mode: 'onTouched',
    criteriaMode: 'all',
    reValidateMode: 'onChange',
    shouldUnregister: true,
    defaultValues,
  });

  const { register, handleSubmit, formState } = formMethods;

  const onSubmit = async (values: FormValues) => {
    const selectedParent = usersSubtree?.find((company) => company.id === values.owner_id);
    if (!selectedParent) return;

    const payload: Record<string, any> = {
      ...emptyToUndefined(values),
      id: thisUser?.id || undefined,
      logistics_ex_id: values.logistics_ex_id || thisUser?.logistics_ex_id,
      email: isEdit && thisUser?.email === values.email ? undefined : values.email,
      company_name: selectedParent.name || thisUser?.company_name,
      send_credentials: values.send_credentials,
    };

    // only add for creation as update does not accept this field
    if (!isEdit) payload.is_group = false;

    let response: User;
    if (isEdit) response = await updateMutation.trigger(payload);
    else response = await createMutation.trigger(payload);

    if (onSave) void onSave(response);
  };

  const isUniqueLogisticsExId = React.useMemo(
    () =>
      // @ts-expect-error
      debounce(async (value: string) => {
        if (!value || (isEdit && value === thisUser?.logistics_ex_id)) return true;
        const groupExists = await existsMutation.trigger({ logistics_ex_id: value });
        return !groupExists;
      }, 200),
    [existsMutation, isEdit, thisUser?.logistics_ex_id],
  );

  const isUniqueEmail = React.useMemo(
    () =>
      // @ts-expect-error
      debounce(async (value: string) => {
        if (!value) return true;
        // This is to avoid sending unnecessary requests when the user is typing the email.
        if (!isValidEmail(value)) return false;
        if (isEdit && value === thisUser?.email) return true;

        // This form is used both for user creation and updates (eg: profile).
        // If the user is changing its data, the user is already saved and the email is already in db.
        // In that case, we check that the email is not in use by another user ONLY IF the user is changing it.
        if (isEdit && value === thisUser?.email) return true;
        const groupExists = await existsMutation.trigger({ email: value });
        return !groupExists;
      }, 200),
    [isEdit, thisUser?.email, existsMutation],
  );

  return (
    <Modal title={<h3>{isEdit ? t('components.userForm.title.edit', { name: thisUser?.name }) : t('components.userForm.title.new')}</h3>} isOpen={isOpen} onClose={onClose}>
      {(!me || isLoading) && <Spinner centered={true} overlay={true} blur={true} />}

      <FormProvider {...formMethods}>
        <form className="w-[500px]" autoCorrect={'off'} autoComplete={'off'} onSubmit={handleSubmit(onSubmit)} noValidate={true}>
          {userId && <input type={'hidden'} {...register('id')} />}
          <div className="flex flex-col gap-6 overflow-y-scroll px-0.5">
            <Input
              className="w-full"
              name={'name'}
              label={t('components.userForm.labels.name')}
              placeholder={t('components.userForm.placeholders.name')}
              registerOptions={{ required: true }}
              autoFocus={false}
            >
              {formState.errors.name?.type === 'required' ? t('components.userForm.errors.required') : null}
            </Input>

            <Input
              name={'email'}
              label={t('components.userForm.labels.email')}
              placeholder={t('components.userForm.placeholders.email')}
              disabled={isEdit && thisUser?.sso_only}
              registerOptions={{ required: true, validate: { isEmail: isValidEmail, isUnique: isUniqueEmail } }}
            >
              {formState.errors.email?.type === 'required' ? t('components.userForm.errors.required') : null}
              {formState.errors.email?.type === 'isUnique' ? t('components.userForm.errors.emailInUse') : null}
              {formState.errors.email?.type === 'isEmail' ? t('components.userForm.errors.invalidEmail') : null}
            </Input>

            <SelectMulti
              className="w-1/2"
              name={'owner_id'}
              label={t('components.userForm.labels.parentGroup')}
              options={availableParents}
              selectable={'one'}
              closeOnChangedValue={true}
              required={true}
              hasClearAll={true}
            />

            <SelectMulti
              className="hidden w-full"
              name={'role'}
              label={t('components.userForm.labels.role')}
              options={availableRoles}
              selectable={'one'}
              closeOnChangedValue={true}
              required={true}
              hasClearAll={true}
            />

            <Input
              className="w-full"
              name={'logistics_ex_id'}
              label={t('components.userForm.labels.logisticsId')}
              placeholder={t('components.userForm.placeholders.logisticsId')}
              registerOptions={{ validate: { isUnique: isUniqueLogisticsExId } }}
              autoFocus={false}
            >
              {formState.errors.logistics_ex_id?.type === 'required' ? t('components.userForm.errors.required') : null}
              {formState.errors.logistics_ex_id?.type === 'isValidLogisticsExId' ? t('components.userForm.errors.invalidLogisticsId') : null}
              {formState.errors.logistics_ex_id?.type === 'isUnique' ? t('components.userForm.errors.logisticsIdInUse') : null}
            </Input>

            {!isEdit && <Checkbox name={'send_credentials'} label={t('components.userForm.labels.sendCredentials')} />}
          </div>

          <div className="mt-8 flex items-center gap-4">
            <Button type="button" className="blue-outlined col-span-2 justify-self-end" onClick={onClose}>
              {t('components.userForm.buttons.back')}
            </Button>
            <Button
              className="blue col-span-2 justify-self-end"
              loading={isLoading}
              disabled={!formState.isValid || !formState.isDirty}
              RightIcon={!isLoading && formState.isSubmitSuccessful ? ICheck : undefined}
              type="submit"
            >
              {t('components.userForm.buttons.save')}
            </Button>
          </div>

          {!!isError && <div className="mt-2 text-red">{getTranslatedError(isError, t) || t('components.userForm.errors.unknown')}</div>}
        </form>
      </FormProvider>
    </Modal>
  );
};
